/** */
import React, { useMemo } from 'react';
import { GridColumn, GridRow } from 'app/member/layouts/grid';
import { SectionCard } from 'app/shared/section-card';
import { LineChart } from 'app/shared/charts/line';
import { formatMonth } from 'utilities/date';
import styles from './channel-adoption.module.css';

const CHANNELS = [
  { key: 'campaign', label: 'Campaign', color: 'var(--blue-primary)' },
  { key: 'transactional', label: 'Transactional', color: 'var(--green-primary)' },
  { key: 'automation', label: 'Automation', color: 'var(--orange-primary)' },
  { key: 'push', label: 'Push', color: 'var(--purple)' },
  { key: 'sms', label: 'SMS', color: 'var(--info)' }
];

const RingChart = ({ current, last, color }) => {
  const r = 14;
  const size = 36;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const total = current + last;
  const raw_pct = total > 0 ? current / total : 0;
  const filled = Math.max(raw_pct * circumference, total > 0 ? circumference * 0.04 : 0);
  const gap = circumference - filled;

  return (
    <svg
      width={size}
      height={size}
      style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}
      aria-hidden="true"
    >
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--neutral-dark-6)" strokeWidth={5} />
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={5}
        strokeDasharray={`${filled.toFixed(2)} ${gap.toFixed(2)}`}
        strokeLinecap="round"
      />
    </svg>
  );
};

export const ChannelAdoption = ({ domains = {} }) => {
  const { chart_data, year_totals, active_tenants } = useMemo(() => {
    const histories = {};
    const totals = {};
    const active = {};

    for (const ch of CHANNELS) {
      const d = domains[ch.key] || {};
      histories[ch.key] = d.usageHistory || [];
      totals[ch.key] = d.yearTotals || {};
      active[ch.key] = d.metrics?.active_sending_tenants?.value ?? 0;
    }

    const spine = histories.campaign;
    if (!spine.length) return { chart_data: [], year_totals: totals, active_tenants: active };

    const data = spine.map((point, i) => {
      const row = { month: formatMonth(point.month) };
      let total = 0;
      for (const ch of CHANNELS) {
        const count = histories[ch.key][i]?.count || 0;
        row[ch.key] = count;
        total += count;
      }
      row.total = total;
      return row;
    });

    return { chart_data: data, year_totals: totals, active_tenants: active };
  }, [domains]);

  return (
    <GridRow num_of_columns={5} auto_flow={false}>
      <GridColumn span={3} auto_columns={false}>
        <SectionCard title="Channel Activity — 13 months">
          <div className={styles.chartWrap}>
            <LineChart
              data={chart_data}
              lines={[
                ...CHANNELS,
                { key: 'total', label: 'Total', color: 'var(--neutral-dark-2)', dashed: true }
              ]}
              xKey="month"
            />
          </div>
        </SectionCard>
      </GridColumn>

      <GridColumn span={2} auto_columns={false}>
        <div className={styles.statStack}>
          {CHANNELS.map((ch) => {
            const { current_year = 0, last_year = 0 } = year_totals[ch.key] || {};
            const yoy = last_year > 0 ? ((current_year - last_year) / last_year) * 100 : null;
            return (
              <div key={ch.key} className={styles.statCard}>
                <RingChart current={current_year} last={last_year} color={ch.color} />
                <div className={styles.statBody}>
                  <div className={styles.statHeader}>
                    <span className={styles.statDot} style={{ background: ch.color }} />
                    <span className={styles.statLabel}>{ch.label}</span>
                    {yoy !== null && (
                      <span
                        className={`${styles.yoyBadge} ${yoy >= 0 ? styles.yoyUp : styles.yoyDown}`}
                      >
                        {yoy >= 0 ? '+' : ''}
                        {yoy.toFixed(0)}%
                      </span>
                    )}
                  </div>
                  <div className={styles.statNumbers}>
                    <span className={styles.currentYear}>{current_year.toLocaleString()}</span>
                    <span className={styles.lastYear}>vs {last_year.toLocaleString()} last yr</span>
                  </div>
                  <div className={styles.adoptionLine}>
                    {(active_tenants[ch.key] || 0).toLocaleString()} tenant
                    {active_tenants[ch.key] === 1 ? '' : 's'} actively sending
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </GridColumn>
    </GridRow>
  );
};
